import React, { Fragment, ReactElement, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { NotableItem, NotableItemsGrid } from "../../types/NotableItems";
import { Galleria } from "primereact/galleria";
import { Image } from "primereact/image";
import { Button } from "primereact/button";
import "./NotableItems.css";

export const NotableItems = (props: NotableItemsGrid): ReactElement => {
    const galleria: any = useRef<Galleria>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [images, setImages] = useState<string[]>([""]);

    const itemTemplate = (item: string) => {
        return <img src={item} style={{ width: "100%", display: "block" }} />;
    };

    const thumbnailTemplate = (item: string) => {
        return <img src={item} style={{ display: "block" }} />;
    };

    const getSeverityClass = (severity: number) => {
        switch (severity) {
            case 1:
            case 2:
                return "severe";
            case 3:
                return "mid";
            case 4:
                return "positive";
            case 5:
                return "low";
            default:
                throw new Error(
                    `Unknown severity passed as argument: ${severity}`
                );
        }
    };

    const getLocaleValue = (i18nKey: string) => {
        return <Trans ns="common" i18nKey={i18nKey} />;
    };

    const loadCarousel = (defect: NotableItem, activeIndex?: number) => {
        if (props.sendAnalyticsEvent) {
            props.sendAnalyticsEvent("defect_images_click");
        }

        setImages(defect.imageUrls.filter((item) => item));
        setActiveIndex(activeIndex ?? 0);
        galleria.current.show();
    };

    const showImagesButton = (defect: NotableItem) => {
        if (props.showImageGrid === true) {
            return <></>;
        }

        const imageUrls = defect.imageUrls.filter((item) => item);

        if (!imageUrls || imageUrls.length === 0) {
            return <></>;
        }

        return (
            <div className="pl-2 ml-auto">
                <Button
                    tooltip="View images"
                    tooltipOptions={{
                        position: "left"
                    }}
                    className="p-button p-component p-button-rounded p-button-text p-button-icon-only"
                    onClick={() => loadCarousel(defect)}
                >
                    <i className="p-button-icon p-c pi pi-images" />
                </Button>
            </div>
        );
    };

    return (
        <Fragment>
            <Galleria
                ref={galleria}
                value={images}
                numVisible={7}
                style={{ maxWidth: "850px" }}
                activeIndex={activeIndex}
                onItemChange={(e) => setActiveIndex(e.index)}
                circular
                fullScreen
                showItemNavigators={images.length > 1}
                showThumbnails={false}
                item={itemTemplate}
                thumbnail={thumbnailTemplate}
            />

            <div
                className={`grid ${
                    !props.showImageGrid
                        ? "pl-6 notable-items mt-3 lg:-mt-3"
                        : "mt-5"
                }`}
            >
                <div className="font-semibold hidden lg:block lg:col-6">
                    {getLocaleValue(
                        "inspectionReport.notableItems.description"
                    )}
                </div>
                <div className="font-semibold hidden lg:block lg:col-4">
                    {getLocaleValue("inspectionReport.notableItems.action")}
                </div>
                <div className="font-semibold hidden lg:block col-2 text-right">
                    {getLocaleValue(
                        "inspectionReport.notableItems.estimatedCost"
                    )}
                </div>

                {props.notableItems.map((defect, index) => {
                    const imageUrls = defect.imageUrls.filter((item) => item);
                    return (
                        <React.Fragment key={`ni-${index}`}>
                            <div className="col-12 pt-0 pb-0 lg:pb-3 lg:col-6 xl:mt-2">
                                <Image
                                    imageClassName="ni-icon"
                                    src={`/notable-item-${getSeverityClass(
                                        defect.importanceOfDefect
                                    )}.svg`}
                                />
                                <div className="flex">
                                    <div className="">{defect.description}</div>
                                    {showImagesButton(defect)}
                                </div>
                            </div>
                            <div className="ni-individual-header pb-0 col-12">
                                {getLocaleValue(
                                    "inspectionReport.notableItems.action"
                                )}
                            </div>
                            <div className="col-12 lg:col-4 pt-0 pb-0 lg:pb-3 xl:mt-2">
                                {defect.action}
                            </div>
                            <div className="ni-individual-header pb-0 col-12">
                                {getLocaleValue(
                                    "inspectionReport.notableItems.estimatedCost"
                                )}
                            </div>
                            <div className="col-12 lg:col-2 pt-0 pb-3 lg:text-right xl:mt-2">
                                {defect.estimatedCostForReport}
                            </div>
                            {!props.showImageGrid &&
                                index + 1 !== props.notableItems.length && (
                                    <div className="border-bottom-1 border-gray-100 col-12 p-0 mb-3" />
                                )}
                            {props.showImageGrid &&
                                imageUrls &&
                                imageUrls.length !== 0 && (
                                    <div className="col-12">
                                        <div className="grid mb-1">
                                            {imageUrls.map(
                                                (imageUrl, imageIndex) => {
                                                    return (
                                                        <div
                                                            key={
                                                                defect.description +
                                                                imageIndex
                                                            }
                                                            className="col-12 sm:col-6 lg:col-3"
                                                        >
                                                            <div className="relative">
                                                                <Image
                                                                    imageClassName="ni-table-image"
                                                                    src={
                                                                        imageUrl
                                                                    }
                                                                    onClick={() =>
                                                                        loadCarousel(
                                                                            defect,
                                                                            imageIndex
                                                                        )
                                                                    }
                                                                />
                                                                <div className="absolute bottom-0 right-0 p-2">
                                                                    <Button
                                                                        className="p-button p-button-plain p-component p-button-rounded p-button-text p-button-icon-only"
                                                                        onClick={() =>
                                                                            loadCarousel(
                                                                                defect,
                                                                                imageIndex
                                                                            )
                                                                        }
                                                                    >
                                                                        <i className="p-button-icon p-c pi pi-window-maximize text-white" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                )}
                        </React.Fragment>
                    );
                })}
            </div>
        </Fragment>
    );
};
